import { makeAutoObservable, runInAction } from "mobx";
import type { RootStore } from "@/stores/RootStore";
import * as translations from "@/shared/i18n";
import type {
  TranslateFunction,
  SupportedLanguage,
} from "@/shared/i18n/translations";

interface SnackbarState {
  message: string;
  type: "success" | "error" | "info" | "warning";
  visible: boolean;
}

/**
 * UiStore - Global UI state management
 *
 * Manages:
 * - Language/locale (replaces LanguageContext)
 * - Translation function
 * - Snackbar notifications
 */
export class UiStore {
  locale: SupportedLanguage = "en";
  snackbar: SnackbarState = { message: "", type: "info", visible: false };

  private _snackbarTimeout: ReturnType<typeof setTimeout> | null = null;

  // Note: root is stored for potential cross-store access in the future
  constructor(private readonly root: RootStore) {
    makeAutoObservable(this, {
      root: false,
      _snackbarTimeout: false,
    } as Record<string, false>);

    // Prevent unused variable warning
    void this.root;

    this._loadLocaleFromStorage();
  }

  /**
   * Translation function - use in components
   * @example const t = uiStore.t; <span>{t("welcome")}</span>
   */
  get t(): TranslateFunction {
    // translations[locale] is already a function (key, ...args) => string
    return translations[this.locale] as TranslateFunction;
  }

  setLocale(locale: SupportedLanguage): void {
    this.locale = locale;
    try {
      localStorage.setItem("apaato_locale", locale);
    } catch {
      // Storage not available
    }
  }

  /**
   * Show a snackbar notification
   * Arrow function to ensure stable 'this' binding when passed around
   */
  showSnackbar = (
    message: string,
    type: "success" | "error" | "info" | "warning" = "info",
    duration = 5000,
  ): void => {
    if (this._snackbarTimeout) {
      clearTimeout(this._snackbarTimeout);
    }

    runInAction(() => {
      this.snackbar = { message, type, visible: true };
    });

    this._snackbarTimeout = setTimeout(() => {
      runInAction(() => {
        this.snackbar.visible = false;
      });
    }, duration);
  };

  /**
   * Hide the snackbar
   * Arrow function to ensure stable 'this' binding when passed around
   */
  hideSnackbar = (): void => {
    if (this._snackbarTimeout) {
      clearTimeout(this._snackbarTimeout);
    }
    this.snackbar.visible = false;
  };

  private _loadLocaleFromStorage(): void {
    try {
      const stored = localStorage.getItem(
        "apaato_locale",
      ) as SupportedLanguage | null;
      if (stored && (stored === "en" || stored === "ja" || stored === "ru")) {
        this.locale = stored;
      }
    } catch {
      // Use default
    }
  }
}
